/**
 * Browser-side cursor walk for the venue upcoming-shows table (WEB-884).
 *
 * `/venue/[slug]` renders one server-fetched page of shows. Before WEB-884 it
 * rendered *only* that page: nothing read `meta.cursor`, so a venue with 41
 * upcoming shows put 20 rows on screen under a header
 * ("20 OF 41 LISTED" — API-1693) that correctly named 21 shows no visitor
 * could reach. This module is the second half: the page size the SSR fetch and
 * every subsequent page share, plus the fetch that advances the cursor.
 *
 * **Why the browser calls the API directly rather than a server action.**
 * `GET /venues/:slug/shows` is a public read behind its own IP-keyed bucket,
 * `venueShowsReadRateLimit` (150 requests / 15 min, API-1718). Proxying the
 * cursor walk through a route handler or a server action would stamp every
 * visitor's clicks with the *deployment's* egress IP, so one popular venue
 * could exhaust the bucket for the whole surface. Going direct
 * keys each walk to the visitor who is doing it, and the response's own
 * `Cache-Control: public, s-maxage=60` header still applies. Both sides of the
 * hop are already provisioned for it: `clt.loci.city` and its staging twin are
 * in `DEFAULT_ALLOWED_ORIGINS` (`@loci/config`'s CORS allowlist), and
 * `NEXT_PUBLIC_API_URL` is in the `connect-src` directive `next.config.js`
 * emits.
 *
 * **Why that bucket is dedicated:** the route used to sit on the SHARED
 * `eventSearchReadRateLimit` (60/15min, drawn on by ~30 public reads — this
 * page's own venue detail and stats reads, `neighborhoods/*`, artists,
 * categories). Once browsers walked this cursor, readers paging a large
 * calendar behind one CGNAT egress would have spent those surfaces' budget
 * too — the coupling API-1674 measured for `/location/reverse`. API-1718 moved
 * the route onto its own bucket, so a cursor walk can no longer 429 them.
 *
 * **This module never degrades a failure into an empty page.** That is the one
 * behaviour it deliberately does NOT share with `lib/server/fetchVenueShows.ts`,
 * whose degraded envelope exists so a cold backend cannot crash RSC render.
 * Here the rows are already on screen; a failed *additional* page is a failed
 * click, and the caller must be able to say so and offer a retry. Silently
 * resolving to `[]` would read as "that was the end of the calendar" — the
 * same class of unbacked claim API-1693 removed from the header.
 *
 * **Not the same shape as `venuePagination.ts`, on purpose.** The `/venues`
 * index (WEB-870) paginates through the URL — a cursor trail, real `<a href>`
 * steps, a full navigation per page — because each page is the whole document
 * and must be linkable and crawlable. This is a section inside a document that
 * is already rendered, and a navigation would reload the venue's hero, stats,
 * map and rail to extend one table, losing the reader's place in it. Different
 * affordances for different units of content, sharing nothing but the word
 * "cursor".
 *
 * @since WEB-884
 */
import { venueShowsResponseSchema, type VenueShowsResponse } from '@loci/validation';
import { API_BASE_URL } from '@/config/api';

/**
 * Rows per page — the SSR first page and every "Load more" page alike.
 *
 * ONE constant on purpose. The header reconciles rendered rows against
 * `meta.total`, so a first page and a subsequent page of different sizes would
 * make the arithmetic a visitor can do on screen ("20 of 41", click, "70 of
 * 41"?) depend on which fetcher produced which rows.
 *
 * 50 rather than the previous 20: the endpoint caps `limit` at 100
 * (`VENUES_MAX_LIMIT`), and a first page well clear of the largest real
 * calendars means most venues need no click at all AND ship their whole
 * calendar in crawlable SSR HTML. The cursor walk is what removes the ceiling;
 * this number only decides how rarely a visitor has to use it.
 */
export const VENUE_SHOWS_PAGE_SIZE = 50;

/** Thrown when a cursor page could not be fetched or could not be trusted. */
export class VenueShowsPageError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'VenueShowsPageError';
  }
}

export interface FetchVenueShowsPageOptions {
  /** Aborts the request when the component unmounts mid-flight. */
  signal?: AbortSignal;
}

/**
 * Fetch the page of upcoming shows that follows `cursor`.
 *
 * Resolves with the parsed envelope. Throws {@link VenueShowsPageError} on a
 * non-2xx response or a body that does not satisfy the wire contract — see the
 * module docblock for why this never resolves to an empty page instead.
 */
export async function fetchVenueShowsPage(
  slug: string,
  cursor: string,
  { signal }: FetchVenueShowsPageOptions = {},
): Promise<VenueShowsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(VENUE_SHOWS_PAGE_SIZE));
  params.set('cursor', cursor);
  const url = `${API_BASE_URL}/api/v1/venues/${encodeURIComponent(slug)}/shows?${params.toString()}`;

  let res: Response;
  try {
    res = await fetch(url, { signal, headers: { accept: 'application/json' } });
  } catch (error) {
    // Re-throw aborts unchanged so callers can tell "we cancelled this" apart
    // from "the network failed" and skip rendering an error for their own
    // teardown.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new VenueShowsPageError('Network error fetching venue shows page', { cause: error });
  }

  if (!res.ok) {
    throw new VenueShowsPageError(`Failed to fetch venue shows page: ${res.status}`);
  }

  const raw: unknown = await res.json().catch((error: unknown) => {
    throw new VenueShowsPageError('Malformed venue shows page body', { cause: error });
  });

  const parsed = venueShowsResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new VenueShowsPageError('Venue shows page failed schema validation', {
      cause: parsed.error,
    });
  }

  return parsed.data;
}
